<template>
    <div id="robot-modal" :class="[{ 'modal fade p-2': true, show: show, close: !show }]">
        <div @click="closeModal()" class="bg-backdrop absolute w-full h-full"></div>
        <div
            class="modal-content w-full flex flex-col relative overflow-hidden"
            :style="[!result || !questionList.length ? { height: 'auto !important' } : {}]"
        >
            <div class="modal-header flex justify-between items-center p-4">
                <h3 class="m-0 shadow-text-none">{{ $t('robot_modal_header') }}</h3>
                <i @click="closeModal()" class="fas fa-times text-xl shadow-text-none cursor-pointer"></i>
            </div>
            <RobotLoader
                v-if="isLoading"
                :show-progress-bar="showRobotProgress"
                :progress="currentProgress"
                :total-progress="numberQuestions"
                :error-progress="errorProgress"
            />
            <div class="modal-body w-full font-size-16 overflow-y-auto">
                <div class="p-4 bg-white">
                    <div class="robot-header-warning w-full mx-auto mb-4 p-3 text-xs text-center">
                        {{ $t('robot_modal_warning_header') }}
                    </div>
                    <div class="mb-4">
                        <div id="robot-modal-tabs" class="btn-group">
                            <a
                                @click="onClickTab('text')"
                                :class="{ active: activeType === 'text', 'font-size-14 md:font-size-16': true }"
                            >
                                <span>{{ $t('robot_modal_tab_message') }}</span>
                            </a>
                            <a
                                @click="onClickTab('upload')"
                                :class="{ active: activeType === 'upload', 'font-size-14 md:font-size-16': true }"
                            >
                                <span>{{ $t('robot_modal_tab_upload') }}</span>
                            </a>
                            <a
                                @click="onClickTab('url')"
                                :class="{ active: activeType === 'url', 'font-size-14 md:font-size-16': true }"
                            >
                                <span>{{ $t('robot_modal_tab_url') }}</span>
                            </a>
                        </div>

                        <div class="flex" style="justify-content: space-between">
                            <label for="robot-textarea" class="font-bold">{{
                                activeType === 'text'
                                    ? $t('robot_modal_topic')
                                    : activeType === 'upload'
                                    ? $t('robot_modal_tab_upload')
                                    : $t('robot_modal_tab_url')
                            }}</label>
                            <div>
                                <span
                                    for="robot-textarea"
                                    class="font-size-12 shadow-text-none"
                                    v-html="
                                        $t(
                                            isTeacher
                                                ? 'robot_modal_question_only_remaining_message'
                                                : 'robot_modal_question_remaining',
                                            {
                                                message: `<span class='robot-error-input' style='font-size: 14px; font-weight: 400;'>${limitAiQuestions}</span>`
                                            }
                                        )
                                    "
                                ></span>
                                <a
                                    v-if="!isTeacher"
                                    @click.prevent="onUpgrade()"
                                    class="font-size-12 shadow-text-none system-primary-text-color"
                                    href="#"
                                >
                                    {{ $t('robot_modal_upgrade') }}
                                </a>
                            </div>
                        </div>
                        <div v-if="activeType === 'text'">
                            <XTextarea
                                @input="topic = $event.target.value"
                                :value="topic"
                                id="robot-textarea"
                                rows="4"
                                maxlength="100"
                                :placeholder="$t('robot_modal_topic_placeholder')"
                                class="mt-1"
                            ></XTextarea>
                        </div>
                        <div v-if="activeType === 'upload'">
                            <PDFUploader
                                @start-upload="handleStartUpload"
                                @end-upload="isLoading = false"
                                @file="getFile"
                            />
                        </div>
                        <div v-if="activeType === 'url'">
                            <URLsInput @urls="getURLs" @invalid="isInvalidURL = $event" class="mt-1" />
                        </div>
                    </div>
                    <div class="robot-options flex flex-col md:flex-row">
                        <div id="box-number-of-questions" class="flex-1 w-full">
                            <label for="number-of-questions" class="font-bold">{{
                                $t('robot_modal_number_of_questions')
                            }}</label>
                            <XInput
                                @input="numberQuestions = $event.target.value"
                                :value="numberQuestions"
                                :only-number="true"
                                id="number-of-questions"
                                class="mt-1"
                            />
                            <p
                                v-if="!+numberQuestions || +numberQuestions > +limitQuestions"
                                class="robot-error-input mt-1 mb-0 shadow-text-none"
                            >
                                <template v-if="!+numberQuestions">
                                    {{ $t('robot_modal_number_of_questions_empty_error') }}
                                </template>
                                <template v-if="+numberQuestions > +limitQuestions">
                                    {{
                                        $t('robot_modal_number_of_questions_over_limit_error', {
                                            limitQuestions: limitQuestions
                                        })
                                    }}
                                </template>
                            </p>
                        </div>
                        <div id="box-number-of-answer" class="flex-1 w-full">
                            <label for="number-of-answer" class="font-bold">{{
                                $t('robot_modal_number_of_answers')
                            }}</label>
                            <XInput
                                @input="numberAnswers = $event.target.value"
                                :value="numberAnswers"
                                :only-number="true"
                                id="number-of-answer"
                                class="mt-1"
                            />
                            <p
                                v-if="!+numberAnswers || +numberAnswers > +limitAnswers"
                                class="robot-error-input mt-1 mb-0 shadow-text-none"
                            >
                                <template v-if="!+numberAnswers">
                                    {{ $t('robot_modal_number_of_answers_empty_error') }}
                                </template>
                                <template v-if="+numberAnswers > +limitAnswers">
                                    {{ $t('robot_modal_number_of_answers_over_limit_error') }}
                                </template>
                            </p>
                        </div>
                        <div id="box-language" class="flex-1 w-full">
                            <label for="language" class="font-bold">{{ $t('robot_modal_language') }}</label>
                            <!-- <XSelect
                                @input="language = $event"
                                :value="language"
                                :options="languageList"
                                :clearable="false"
                                :searchable="false"
                                id="language"
                                class="mt-1"
                            /> -->
                            <div class="relative">
                                <Selection
                                    v-model="language"
                                    id="language-select"
                                    class="language-select-input w-full h-full mt-1"
                                >
                                    <option v-for="(lang, index) in languageList" :key="index">{{ lang }}</option>
                                </Selection>
                            </div>
                        </div>
                    </div>
                    <div class="robot-options flex flex-col md:flex-row items-center mt-4">
                        <div id="box-difficult-level" class="flex-1 w-full">
                            <label for="difficult-level" class="font-bold">{{ $t('robot_modal_difficult') }}</label>
                            <SliderBar
                                @change="difficult = $event"
                                :value="difficult"
                                :rangeText="['Easy', 'Normal', 'Hard']"
                                :min="0"
                                :max="10"
                                :dotSize="30"
                                color="#000"
                                id="difficult-level"
                                class="mt-8"
                            ></SliderBar>
                        </div>
                        <div id="box-age" class="flex-1 w-full">
                            <label for="age" class="font-bold">{{ $t('robot_modal_age') }}</label>
                            <SliderBar
                                @change="age = $event"
                                :value="age"
                                :min="0"
                                :max="99"
                                :dotSize="30"
                                color="#000"
                                id="age"
                                class="mt-8"
                            ></SliderBar>
                        </div>
                    </div>
                </div>

                <div v-if="result && questionList.length" class="question-list-box p-4">
                    <div
                        v-for="(question, questionIndex) in questionList"
                        :key="questionIndex"
                        :class="[{ 'mt-4': questionIndex != 0 }]"
                    >
                        <div class="flex justify-between items-center">
                            <h3 class="m-0 shadow-text-none">Question {{ questionIndex + 1 }}</h3>
                            <div @click="removeQuestion(questionIndex)" class="close text-red cursor-pointer">
                                &times;
                            </div>
                        </div>
                        <div class="question-box mt-2 p-4">
                            <div class="question-input-box flex items-center mb-4">
                                <XInput
                                    @input="question.title = $event.target.value"
                                    :value="question.title"
                                    type="text"
                                    class="form-control"
                                    placeholder="Question title"
                                />
                                <XButton
                                    @click="collapseRaw(`raw-box-${questionIndex}`)"
                                    :data-target="`#raw-box-${questionIndex}`"
                                    class="raw-btn p-0"
                                    data-toggle="collapse"
                                    bg-color="#6c757d"
                                >
                                    <i class="far fa-file-alt shadow-text-none"></i>
                                </XButton>
                            </div>
                            <div :id="`raw-box-${questionIndex}`" class="raw-box overflow-hidden">
                                <div v-html="question.raw" class="p-4"></div>
                            </div>
                            <div class="mb-4">
                                <label class="block mb-4">Answers Required</label>
                                <div class="answer-required-box flex items-center">
                                    <XCheckbox
                                        @input="question.answers_required = 'one'"
                                        :id="`answer-one-${questionIndex}`"
                                        :name="`answer-required-${questionIndex}`"
                                        :checked="!question.answers_required || question.answers_required == 'one'"
                                        class="shadow-text-none"
                                        type="radio"
                                        value="one"
                                        size="lg"
                                        theme="red"
                                    >
                                        One
                                    </XCheckbox>

                                    <XCheckbox
                                        @input="checkedAnswersRequired('multiple', questionIndex)"
                                        :id="`answer-multiple-${questionIndex}`"
                                        :name="`answer-required-${questionIndex}`"
                                        :checked="question.answers_required == 'multiple'"
                                        :disabled="question.answers.filter(e => e.is_correct).length <= 1"
                                        class="shadow-text-none"
                                        type="radio"
                                        value="multiple"
                                        size="lg"
                                        theme="red"
                                    >
                                        Multiple
                                    </XCheckbox>
                                </div>
                            </div>
                            <div
                                v-for="(answer, answerIndex) in question.answers"
                                :key="answerIndex"
                                class="answer-box flex items-center mb-2 p-2"
                            >
                                <XCheckbox
                                    @input="
                                        answer.is_correct = $event.target.checked
                                        checkedCorrectAnswer(questionIndex)
                                    "
                                    :id="`choice-${questionIndex}-${answerIndex}`"
                                    :checked="answer.is_correct"
                                    :square="true"
                                    :showContent="false"
                                    :disabled="
                                        answer.is_correct && question.answers.filter(e => e.is_correct).length <= 1
                                    "
                                    class="shadow-text-none"
                                    type="checkbox"
                                    size="lg"
                                    theme="dark"
                                />
                                <XInput
                                    @input="answer.title = $event.target.value"
                                    :value="answer.title"
                                    :placeholder="`Choice ${answerIndex + 1}`"
                                />
                                <XButton
                                    @click="removeAnswer(questionIndex, answerIndex)"
                                    :disabled="question.answers.length <= 1"
                                    class="btn btn-danger p-0"
                                >
                                    <i class="far fa-trash-alt shadow-text-none"></i>
                                </XButton>
                            </div>
                            <XButton
                                @click="addAnswer(questionIndex)"
                                class="add-answer-btn w-full"
                                bg-color="transparent"
                                color="#6c757d"
                            >
                                <i class="fas fa-plus shadow-text-none"></i>
                            </XButton>
                        </div>
                    </div>
                </div>
            </div>
            <div class="modal-footer flex flex-col sm:flex-row justify-end items-center p-4">
                <XButton
                    @click="generateQuestion()"
                    :disabled="disabledGenerate"
                    class="w-full"
                    bg-color="#17a2b8"
                    font-weight="400"
                >
                    <i class="fas fa-magic"></i>
                    {{ $t('robot_modal_button_generate') }}
                </XButton>

                <XButton
                    v-if="result && questionList.length"
                    @click="addToQuestion()"
                    :disabled="disabledAdd"
                    class="w-full sm:w-1/2 sm:ml-2 mt-2 sm:mt-0"
                    bg-color="#28a745"
                    font-weight="400"
                >
                    <i class="fas fa-plus"></i>
                    {{ $t('robot_modal_button_add_to_question') }}
                </XButton>
            </div>
        </div>

        <RobotSorryAlert
            v-if="showSorryAlert"
            @close="showSorryAlert = false"
            :message="$t('robot_sorry_modal_description', { success: aiSuccessGeneratedQuestion })"
            id="robot-sorry-modal"
        />

        <UpgradeAiQuestionModal
            v-if="isShowUpgradeAiQuestionModal"
            @success="onSuccess"
            @close="isShowUpgradeAiQuestionModal = false"
            ref="upgradeAiQuestion"
        />
    </div>
</template>

<script>
import appConfig from '@/app.config'
import Request from '@/lib/request.js'
import XTextarea from '@/components/v2/XTextarea'
import XInput from '@/components/v2/XInput'
import SliderBar from '@/components/SliderBar'
import XButton from '@/components/v2/XButton'
import XCheckbox from '@/components/v2/XCheckbox'
import RobotLoader from '@/components/quiz-creator/question-section/RobotLoader'
import RobotSorryAlert from '@/components/quiz-creator/question-section/RobotSorryAlert'
import IntroHint from '@/lib/intro.js'
import introJsData from '@/mixins/introjs-data'
import { startIntroAIGenerate } from '@/lib/intro.js'
import { currentUser, getToken } from '@/lib/user.js'
import UpgradeAiQuestionModal from '@/components/UpgradeAiQuestionModal'
import PDFUploader from '@/components/PDFUploader'
import URLsInput from '@/components/URLsInput'
import popup from '@/lib/popup'
import { load } from 'recaptcha-v3'
import Cookies from 'js-cookie'
const { jsonrepair } = require('jsonrepair')
import Selection from '@/components/v2/Selection'

export default {
    components: {
        XTextarea,
        XInput,
        SliderBar,
        XButton,
        XCheckbox,
        RobotLoader,
        RobotSorryAlert,
        UpgradeAiQuestionModal,
        PDFUploader,
        URLsInput,
        Selection
    },

    mixins: [introJsData],

    data() {
        return {
            show: true,
            isLoading: false,
            topic: '',
            numberQuestions: 4,
            languageList: [],
            language: null,
            difficult: 5,
            age: [0, 99],
            numberAnswers: 3,
            result: false,
            questionList: [],
            showRobotProgress: false,
            currentProgress: 0,
            errorProgress: [],
            ignoreQuestions: [],
            limitQuestions: 30,
            limitAiQuestions: 5,
            limitAnswers: 10,
            showSorryAlert: false,
            introHint: new IntroHint(),
            isShowUpgradeAiQuestionModal: false,

            aiSuccessGeneratedQuestion: 0,

            activeType: 'text',
            fileContent: '',
            urls: [],
            isInvalidURL: false
        }
    },

    created() {
        this.introHint.removeHints()
        setTimeout(() => {
            startIntroAIGenerate(this.stepTour7)
        }, 500)
        this.getLang()
    },

    computed: {
        disabledGenerate() {
            const requiredFields = {
                text: this.topic,
                upload: this.fileContent,
                url: !this.isInvalidURL && this.urls.length
            }

            return (
                !requiredFields[this.activeType] ||
                !+this.numberQuestions ||
                +this.numberQuestions > +this.limitQuestions ||
                !+this.numberAnswers ||
                +this.numberAnswers > +this.limitAnswers
            )
        },

        disabledAdd() {
            return this.questionList.filter(question => !question.title).length > 0 ? true : false
        },

        isTeacher() {
            return ['teacher', 'teacher_plus'].includes(currentUser().role)
        },

        isTeacherPlus() {
            return currentUser().role === 'teacher_plus'
        }
    },

    methods: {
        onSuccess() {
            this.getLimitAiQuestion()
        },

        onUpgrade() {
            this.isShowUpgradeAiQuestionModal = true
        },

        popupConfirmGenerate() {
            return new Promise(resolve => {
                popup.confirm(
                    this.$t('robot_modal_warning_re_generate'),
                    () => {
                        resolve(true)
                    },
                    () => {
                        resolve(false)
                    }
                )
            })
        },

        closeModal() {
            if (this.isLoading) {
                return
            }

            this.show = false

            setTimeout(() => {
                this.$emit('close')
                this.introHint.register(this.hintsQuestionList)
            }, 300)
        },

        async getLang() {
            const languageMap = {
                en: 'English',
                no: 'Norwegian',
                se: 'Swedish',
                dk: 'Danish',
                fr: 'French',
                de: 'German',
                es: 'Spanish'
            }

            this.languageList = appConfig.supportedLanguages.map(e => languageMap[e] || e)

            let lang = 'en'
            if (Cookies.get('lang')) {
                lang = Cookies.get('lang')
            }

            this.language = languageMap[lang] || 'English'
            await this.getLimitAiQuestion()
        },

        getRecaptchaSiteKey() {
            return process.env.VUE_APP_RECAPTCHA_SITE_KEY
        },

        async fetchWithTimeout(url, options) {
            const { timeout = 20000 } = options

            const controller = new AbortController()
            const id = setTimeout(() => controller.abort(), timeout)
            const signal = controller.signal

            const fetchIns = await fetch(url, { ...options, signal })

            clearTimeout(id)

            return fetchIns
        },

        async generateQuestion() {
            if (this.disabledGenerate) return

            if (+this.numberQuestions > +this.limitAiQuestions) {
                if (this.isTeacherPlus) {
                    return popup.alert(this.$t('robot_modal_alert_limit_question'))
                }

                popup.confirm(
                    this.$t('robot_modal_alert_limit_question'),
                    () => {
                        this.isShowUpgradeAiQuestionModal = true
                    },
                    () => {},
                    {
                        customButtons: {
                            ok: {
                                text: this.$t('robot_modal_button_add_purchase'),
                                className: 'btn font-opensans text-md system-button-color system-button-text-color'
                            }
                        }
                    }
                )

                return
            }

            if (this.result && this.questionList.length) {
                const confirm = await this.popupConfirmGenerate()

                if (!confirm) {
                    return
                }
            }

            this.result = false
            this.isLoading = true
            this.questionList = []
            this.showRobotProgress = true
            this.currentProgress = 0
            this.errorProgress = []
            this.ignoreQuestions = []

            const path = {
                text: 'generate-questions',
                upload: 'generate-questions-doc',
                url: 'generate-questions-url'
            }

            const url = `${process.env.VUE_APP_CHATGPT_BASE_URL}/${path[this.activeType]}?topic=${
                this.topic
            }&question=${this.numberQuestions}&answer=${this.numberAnswers}&language=${this.language}&age=${
                this.age[0]
            }-${this.age[1]}&difficulty=${this.difficult}`

            if (this.activeType === 'text') {
                for (let i = 1; i <= this.numberQuestions; i++) {
                    try {
                        const recaptcha = await load(this.getRecaptchaSiteKey(), {
                            useRecaptchaNet: true,
                            autoHideBadge: true
                        })

                        const token = await recaptcha.execute('validate_captcha', { action: 'validate_captcha' })
                        const request = new Request({
                            headers: {
                                Authorization: `Bearer ${getToken()}`
                            }
                        })
                        const generate = await request.post(url, {
                            captcha_token: token,
                            ignore_questions: this.ignoreQuestions
                        })

                        if (generate.status === 200) {
                            this.currentProgress++
                            this.questionList.push(generate.data.data.questions)
                            this.ignoreQuestions = [...this.ignoreQuestions, generate.data.data.questions.question]
                        }
                    } catch {
                        this.currentProgress++
                        this.errorProgress.push(i)
                    }
                }
            }

            if (this.activeType === 'upload') {
                try {
                    const recaptcha = await load(this.getRecaptchaSiteKey(), {
                        useRecaptchaNet: true,
                        autoHideBadge: true
                    })

                    const token = await recaptcha.execute('validate_captcha', { action: 'validate_captcha' })

                    const formData = new FormData()
                    formData.append('captcha_token', token)
                    formData.append('file_content', this.fileContent)

                    const response = await this.fetchWithTimeout(url, {
                        method: 'POST',
                        body: formData,
                        headers: {
                            Authorization: `Bearer ${getToken()}`
                        }
                    })

                    const reader = response.body.pipeThrough(new window.TextDecoderStream()).getReader()

                    let questionData = {}
                    let streamOutput = ''

                    while (reader) {
                        let stream = await reader.read()

                        if (typeof stream.value === 'string' && stream.value.includes('tokens_less_than_minimum')) {
                            this.toastError(this.$t('tokens_less_than_minimum'))
                            break
                        }

                        if (typeof stream.value === 'string' && stream.value.includes('tokens_greater_than_maximum')) {
                            this.toastError(this.$t('tokens_greater_than_maximum'))
                            break
                        }

                        if (stream.done) break

                        streamOutput += stream.value

                        try {
                            questionData = JSON.parse(jsonrepair(streamOutput))
                            if (questionData && questionData.questions) {
                                this.currentProgress = questionData.questions.length
                            }
                        } catch (e) {
                            window.console.error('Error parsing JSON', e)
                        }
                    }

                    this.questionList = [...this.questionList, ...this.transformQuestions(questionData)]
                } catch (err) {
                    this.showSorryAlert = true

                    if (err.name === 'AbortError') {
                        this.toastError(this.$t('request_timeout'))
                    }
                }
            }

            if (this.activeType === 'url') {
                try {
                    const recaptcha = await load(this.getRecaptchaSiteKey(), {
                        useRecaptchaNet: true,
                        autoHideBadge: true
                    })

                    const token = await recaptcha.execute('validate_captcha', { action: 'validate_captcha' })

                    const response = await this.fetchWithTimeout(url, {
                        method: 'POST',
                        headers: {
                            Authorization: `Bearer ${getToken()}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            captcha_token: token,
                            urls: this.urls
                        })
                    })

                    const reader = response.body.pipeThrough(new window.TextDecoderStream()).getReader()

                    let questionData = {}
                    let streamOutput = ''

                    while (reader) {
                        let stream = await reader.read()

                        if (typeof stream.value === 'string' && stream.value.includes('tokens_less_than_minimum')) {
                            this.toastError(this.$t('tokens_less_than_minimum'))
                            break
                        }

                        if (typeof stream.value === 'string' && stream.value.includes('tokens_greater_than_maximum')) {
                            this.toastError(this.$t('tokens_greater_than_maximum'))
                            break
                        }

                        if (stream.done) break

                        streamOutput += stream.value

                        try {
                            questionData = JSON.parse(jsonrepair(streamOutput))
                            if (questionData && questionData.questions) {
                                this.currentProgress = questionData.questions.length
                            }
                        } catch (e) {
                            window.console.error('Error parsing JSON', e)
                        }
                    }

                    this.questionList = [...this.questionList, ...this.transformQuestions(questionData)]
                } catch (err) {
                    this.showSorryAlert = true

                    if (err.name === 'AbortError') {
                        this.toastError(this.$t('request_timeout'))
                    }
                }
            }

            this.questionList = this.questionList.map(e => {
                return {
                    title: e.question,
                    question_type: 'basic',
                    answers_count: 1,
                    answers_required: 'one',
                    status: true,
                    tags: [],
                    answers: e.answers.map((answer, answerIndex) => {
                        return {
                            title: answer,
                            image: '',
                            is_correct: answerIndex === e.correct_answer_index ? true : false,
                            score: 0,
                            data: {
                                sorting_choice: [],
                                accept_percentage: 0,
                                completely_correct: true
                            }
                        }
                    }),
                    raw: Array.isArray(e.raw) ? e.raw.join('<br>') : e.raw
                }
            })

            await this.setAiQuestion(this.questionList.length)

            this.clearInput()

            setTimeout(() => {
                this.result = true
                this.isLoading = false

                if (this.errorProgress.length) {
                    this.aiSuccessGeneratedQuestion = this.numberQuestions - this.errorProgress.length

                    this.showSorryAlert = true
                }
            }, 500)
        },

        async getLimitAiQuestion() {
            this.isLoading = true

            await this.$store.dispatch('getLimitAiQuestion').then(res => {
                this.limitAiQuestions = res.limit
                this.isLoading = false
            })
        },

        async setAiQuestion(numberSuccess) {
            await this.$store
                .dispatch('setAiQuestionByUser', {
                    question: numberSuccess,
                    failed: this.numberQuestions - numberSuccess
                })
                .then(res => {
                    this.limitAiQuestions = res.limit
                    this.isLoading = false
                })
        },

        collapseRaw(rawBox) {
            rawBox = document.querySelector(`#${rawBox}`)
            rawBox.classList.toggle('active')

            if (rawBox.style.maxHeight) {
                rawBox.style.maxHeight = null
            } else {
                rawBox.style.maxHeight = rawBox.scrollHeight + 'px'
            }
        },

        checkedAnswersRequired(value, questionIndex) {
            if (this.questionList[questionIndex].answers.filter(e => e.is_correct).length <= 1) {
                value = 'one'
            }

            this.questionList[questionIndex].answers_required = value
        },

        checkedCorrectAnswer(questionIndex) {
            this.questionList[questionIndex].answers_count = this.questionList[questionIndex].answers.filter(
                e => e.is_correct
            ).length

            if (this.questionList[questionIndex].answers.filter(e => e.is_correct).length > 1) return

            this.checkedAnswersRequired('one', questionIndex)
        },

        removeQuestion(questionIndex) {
            this.questionList.splice(questionIndex, 1)
        },

        addAnswer(questionIndex) {
            this.questionList[questionIndex].answers.push({
                title: '',
                is_correct: false,
                score: 0,
                data: {
                    sorting_choice: [],
                    accept_percentage: 0,
                    completely_correct: true
                }
            })
        },

        removeAnswer(questionIndex, answerIndex) {
            this.questionList[questionIndex].answers.splice(answerIndex, 1)

            if (!this.questionList[questionIndex].answers.length) return

            const filterCorrect = this.questionList[questionIndex].answers.filter(e => e.is_correct)

            if (filterCorrect.length) return

            this.questionList[questionIndex].answers[0].is_correct = true
        },

        addToQuestion() {
            if (this.disabledAdd) return

            this.isLoading = true
            this.showRobotProgress = false

            this.questionList = this.questionList.map(question => {
                // Add image keys
                question.answers = question.answers.map(answer => {
                    answer.image = ''
                    answer.image_coordinates = []
                    answer.image_original = ''
                    answer.image_source = ''

                    return answer
                })

                return {
                    ...question,
                    answers: question.answers.filter(answer => answer.title)
                }
            })

            this.$emit('add', this.questionList)
            this.toastSuccess(this.$t('robot_modal_alert_success'))
            this.isLoading = false

            this.closeModal()
        },

        handleStartUpload() {
            this.showRobotProgress = false
            this.isLoading = true
        },

        getFile(type, data) {
            this.fileContent = data

            if (type === 'pdf') {
                this.fileContent = data.map(item => item.text).join('')
            }
        },

        getURLs(urls) {
            this.urls = urls
        },

        onClickTab(type) {
            if (this.activeType === type) return

            this.activeType = type

            this.clearInput()
        },

        transformQuestions(data) {
            return data.questions.map(question => {
                const correctAnswerIndex = question.answers.findIndex(answer => answer.is_correct)

                const raw =
                    `${question.question}<br>` +
                    question.answers
                        .map((answer, index) => {
                            const letter = String.fromCharCode(65 + index) // Convert index to A, B, C, ...
                            return `${letter}: ${answer.title}`
                        })
                        .join('<br>')

                return {
                    question: question.question,
                    correct_answer_index: correctAnswerIndex,
                    answers: question.answers.map(answer => answer.title),
                    raw: raw
                }
            })
        },

        clearInput() {
            this.topic = ''
            this.fileContent = ''
            this.urls = []
        }
    }
}
</script>

<style lang="scss">
#robot-modal {
    > .modal-content {
        max-width: 800px;
        max-height: 100%;

        .modal-body {
            background-color: #e6eaf2;
            #robot-textarea {
                text-align: left;

                &::placeholder {
                    text-align: left;
                }
            }

            .robot-header-warning {
                border-radius: 1rem;
                background-color: #fccb19;
                color: #fff;
            }

            .robot-options {
                gap: 1rem;
            }

            .x-select .vs__selected {
                color: #000;
                text-shadow: none;
            }

            .question-list-box {
                background-color: #e6ebf1;

                .close {
                    font-size: 1.5rem;
                    font-weight: 700;
                    line-height: 1;
                    opacity: 0.5;
                }

                .question-box {
                    border-radius: 0.25rem;
                    background-color: #fff;

                    .question-input-box {
                        column-gap: 1rem;
                        height: 40px;

                        .raw-btn {
                            min-width: unset;
                            height: 100%;
                            aspect-ratio: 1 / 1;
                        }
                    }

                    .raw-box {
                        max-height: 0;
                        border: 1px solid rgba(232, 232, 232, 0);
                        border-radius: 0.25rem;
                        line-height: 2;
                        transition: max-height 0.3s, margin 0.3s, border-color 0.3s;

                        &.active {
                            margin-bottom: 1rem;
                            border-color: rgba(232, 232, 232, 1);
                        }
                    }

                    .answer-required-box {
                        column-gap: 1rem;
                    }

                    .answer-box {
                        column-gap: 1rem;
                        height: 60px;
                        border: 1px solid #e8e8e8;
                        border-radius: 0.25rem;

                        button {
                            min-width: unset;
                            height: 100%;
                            aspect-ratio: 1 / 1;
                        }

                        > div:last-child {
                            column-gap: 1rem;
                        }
                    }

                    .add-answer-btn {
                        height: 60px;
                        border: 1px solid #e8e8e8;
                        border-radius: 0.25rem;
                    }
                }

                .empty-container {
                    min-height: unset !important;
                }
            }

            .robot-error-input {
                font-size: 80%;
                color: #dc3545;
            }

            #robot-modal-tabs {
                width: 100%;
                border-radius: 0;
                border-top: none;
                border-right: none;
                border-left: none;

                a.active {
                    border-top-left-radius: 0.3rem;
                    border-top-right-radius: 0.3rem;
                }
            }
        }
    }
}
</style>
